// packages/web/src/hooks/useFeatureFlag.ts
// Feature flag system for graceful degradation

import { useState, useEffect } from 'react';

export interface FeatureFlag {
  name: string;
  enabled: boolean;
  reason?: string;
  lastChecked?: number;
}

// In-memory cache for feature flags
const flagCache = new Map<string, FeatureFlag>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to check if a feature is enabled
 * Falls back to enabled if API is unavailable
 */
export function useFeatureFlag(flagName: string, defaultEnabled = true): boolean {
  const [isEnabled, setIsEnabled] = useState(defaultEnabled);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkFeatureFlag(flagName, defaultEnabled)
      .then(setIsEnabled)
      .catch(() => {
        // On error, use default (graceful degradation)
        console.warn(
          `[FEATURE_FLAG] Failed to check flag ${flagName}, using default: ${defaultEnabled}`,
        );
        setIsEnabled(defaultEnabled);
      })
      .finally(() => setIsLoading(false));
  }, [flagName, defaultEnabled]);

  return isEnabled;
}

/**
 * Check feature flag status with caching and fallbacks
 */
export async function checkFeatureFlag(flagName: string, defaultEnabled = true): Promise<boolean> {
  // Check cache first
  const cached = flagCache.get(flagName);
  if (cached && Date.now() - (cached.lastChecked || 0) < CACHE_DURATION) {
    return cached.enabled;
  }

  try {
    // Try to fetch from API
    const response = await fetch(`/api/features/flags/${flagName}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      // Don't fail if auth is missing
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      const flag: FeatureFlag = {
        name: flagName,
        enabled: data.enabled ?? defaultEnabled,
        reason: data.reason,
        lastChecked: Date.now(),
      };

      flagCache.set(flagName, flag);
      return flag.enabled;
    }

    // API returned error, fall back to cache or default
    if (cached) {
      console.warn(`[FEATURE_FLAG] API error for ${flagName}, using cached value`);
      return cached.enabled;
    }
  } catch (error) {
    console.warn(`[FEATURE_FLAG] Network error checking ${flagName}:`, error);

    // Network error, check cache
    if (cached) {
      console.warn(`[FEATURE_FLAG] Using cached value for ${flagName}`);
      return cached.enabled;
    }
  }

  // No cache and API failed, use default (graceful degradation)
  console.warn(`[FEATURE_FLAG] No cached value for ${flagName}, using default: ${defaultEnabled}`);
  return defaultEnabled;
}

/**
 * Manually set a feature flag (for testing or admin override)
 */
export function setFeatureFlag(flagName: string, enabled: boolean, reason?: string): void {
  const flag: FeatureFlag = {
    name: flagName,
    enabled,
    reason,
    lastChecked: Date.now(),
  };

  flagCache.set(flagName, flag);
  console.log(`[FEATURE_FLAG] Manually set ${flagName} to ${enabled}`);
}

/**
 * Clear feature flag cache
 */
export function clearFeatureFlagCache(): void {
  flagCache.clear();
  console.log('[FEATURE_FLAG] Cache cleared');
}

/**
 * Get all cached feature flags
 */
export function getCachedFeatureFlags(): FeatureFlag[] {
  return Array.from(flagCache.values());
}

/**
 * Predefined feature flags for common features
 */
export const FEATURE_FLAGS = {
  CART: 'cart',
  ADDRESSES: 'addresses',
  MARKETPLACE: 'marketplace',
  CHECKOUT: 'checkout',
  ORDERS: 'orders',
  PROFILE: 'profile',
  NOTIFICATIONS: 'notifications',
  ANALYTICS: 'analytics',
  SEARCH: 'search',
  REVIEWS: 'reviews',
} as const;

export type FeatureFlagName = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];
